import { useCallback, useRef, useState } from "react";

import { observeActionEvent } from "../data/repository";
import { isOutcomeUnknownError } from "./useCommandRunner";

interface QueueChain {
  promise: Promise<unknown>;
  inFlight: number;
  brokenKeys: Set<string>;
  failedIntentIds: Set<string>;
  keyDepth: Map<string, number>;
}

interface QueueRecovery<T> {
  teamId: string;
  actionId: string;
  resolve: (committedRevision: number | null) => T | undefined;
}

interface QueueOptions<T> {
  key?: string;
  chainKey?: string;
  intentId?: string;
  dependsOn?: readonly string[];
  onChainFailure?: () => void;
  recovery?: QueueRecovery<T>;
  acknowledgement?: Promise<void>;
  finishStatusOnCommit?: boolean;
  onAcknowledged?: () => void;
}

interface QueueIntentToken {
  markSending: () => void;
  markAwaitingSnapshot: () => void;
  markOutcomeUnknown: (retry: () => void) => void;
  finish: () => void;
}

export interface SerializedGameQueue {
  enqueue: <T>(
    command: () => Promise<T>,
    options?: QueueOptions<T>,
  ) => Promise<T | false>;
  queueDepth: number;
}

export function eventRecovery(
  teamId: string,
  actionId: string,
  onCommitted?: (revision: number) => void,
): QueueRecovery<boolean> {
  return {
    teamId,
    actionId,
    resolve: (revision) => {
      if (onCommitted && revision === null) return undefined;
      if (revision !== null) onCommitted?.(revision);
      return true;
    },
  };
}

function browserIsOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function useSerializedGameQueue({
  beginIntent,
}: {
  beginIntent?: (key: string) => QueueIntentToken;
}): SerializedGameQueue {
  const [queueDepth, setQueueDepth] = useState(0);
  const chainRef = useRef<QueueChain>({
    promise: Promise.resolve(),
    inFlight: 0,
    brokenKeys: new Set(),
    failedIntentIds: new Set(),
    keyDepth: new Map(),
  });

  const enqueue = useCallback(
    <T>(
      command: () => Promise<T>,
      options: QueueOptions<T> = {},
    ): Promise<T | false> => {
      const chain = chainRef.current;
      const {
        key,
        chainKey,
        intentId,
        dependsOn,
        onChainFailure,
        recovery,
        acknowledgement,
        finishStatusOnCommit,
        onAcknowledged,
      } = options;
      const intent = key ? beginIntent?.(key) : undefined;
      chain.inFlight += 1;
      if (chainKey) {
        chain.keyDepth.set(chainKey, (chain.keyDepth.get(chainKey) ?? 0) + 1);
      }
      setQueueDepth(chain.inFlight);

      const execute = async (): Promise<T | false> => {
        if (chainKey && chain.brokenKeys.has(chainKey)) return false;
        if (
          dependsOn?.some((dependencyId) =>
            chain.failedIntentIds.has(dependencyId),
          )
        ) {
          return false;
        }
        if (browserIsOffline()) return false;
        let stopObserving: (() => void) | null = null;
        try {
          while (true) {
            intent?.markSending();
            try {
              return await command();
            } catch (error) {
              if (!isOutcomeUnknownError(error)) throw error;
              type RecoveryDecision =
                { kind: "retry" } | { kind: "committed"; result: T };
              let settle!: (value: RecoveryDecision) => void;
              let requested = false;
              const decision = new Promise<RecoveryDecision>((resolve) => {
                settle = resolve;
              });
              const retry = () => {
                if (requested) return;
                requested = true;
                settle({ kind: "retry" });
              };
              stopObserving?.();
              stopObserving = null;
              if (recovery) {
                stopObserving = observeActionEvent(
                  recovery.teamId,
                  recovery.actionId,
                  (revision) => {
                    if (requested) return;
                    const result = recovery.resolve(revision);
                    if (result === undefined) return;
                    requested = true;
                    settle({ kind: "committed", result });
                  },
                );
              }
              intent?.markOutcomeUnknown(retry);
              const outcome = await decision;
              if (outcome.kind === "committed") return outcome.result;
            }
          }
        } finally {
          stopObserving?.();
        }
      };

      const transport = chain.promise.then(execute);
      chain.promise = transport.catch(() => {});

      const settleTransport = (failed: boolean) => {
        if (failed) {
          if (chainKey) chain.brokenKeys.add(chainKey);
          if (intentId) chain.failedIntentIds.add(intentId);
          onChainFailure?.();
        }
        chain.inFlight -= 1;
        setQueueDepth(chain.inFlight);
        if (chainKey) {
          const remaining = Math.max(
            0,
            (chain.keyDepth.get(chainKey) ?? 1) - 1,
          );
          if (remaining === 0) {
            chain.keyDepth.delete(chainKey);
            chain.brokenKeys.delete(chainKey);
          } else {
            chain.keyDepth.set(chainKey, remaining);
          }
        }
        if (chain.inFlight === 0) chain.failedIntentIds.clear();
      };

      return transport.then(
        (result) => {
          const failed = result === false || result === null;
          settleTransport(failed);
          if (failed) {
            intent?.finish();
          } else if (!acknowledgement) {
            onAcknowledged?.();
            intent?.finish();
          } else if (finishStatusOnCommit) {
            intent?.finish();
            void acknowledgement.finally(() => onAcknowledged?.());
          } else {
            intent?.markAwaitingSnapshot();
            void acknowledgement.finally(() => {
              onAcknowledged?.();
              intent?.finish();
            });
          }
          return result;
        },
        (error: unknown) => {
          settleTransport(true);
          intent?.finish();
          throw error;
        },
      );
    },
    [beginIntent],
  );

  return { enqueue, queueDepth };
}
